import { captureException } from "@/lib/monitoring";

/**
 * Inertia 3's router methods (`router.get/post/reload/visit`, …) return
 * void, so a hard visit failure cannot be caught at the call sites. Inertia
 * instead fires two cancelable events on `document`, and leaves the
 * `inertia:networkError` event unprevented it ends its request chain with
 * an unhandled `Promise.reject` — a contextless rejection in the error
 * service (issue #410). This module is the central seam: it reports both
 * hard failure kinds with operation context and prevents the one event
 * whose default is the unhandled rejection.
 */

/**
 * Everything a report carries is non-personal metadata: the URL is reduced
 * to its path, because query strings can carry user input (search text) —
 * the same privacy rule `lib/monitoring.ts` enforces via `beforeSend`.
 */
function pathOnly(url: string | undefined): string | undefined {
  return url?.split("?")[0];
}

function report(error: unknown, context: Record<string, string | number>): void {
  captureException(error, { scope: "router", ...context });
  // Keeps the failure visible where the error service is silent (dev/test
  // have no Sentry DSN); the default would be a bare unhandled rejection.
  console.error(error);
}

/**
 * A fresh Error carrying only the message, never the original value: an
 * Error subclass from Inertia can hold the full URL (query string included)
 * as an own property, and Sentry serializes plain-object values wholesale
 * into the event — both would ship query-string user input to the ingest.
 * The original stays local through the console line in `report`.
 */
function asReportableError(error: unknown): Error {
  if (error instanceof Error) {
    return new Error(error.message);
  }

  return new Error("Inertia visit failed without an error detail");
}

function onNetworkError(event: Event): void {
  const error = (event as CustomEvent<{ error?: unknown }>).detail?.error;
  const shaped = error as { url?: string; code?: unknown } | undefined;
  const url = pathOnly(shaped?.url);

  report(asReportableError(error), {
    operation: "networkError",
    ...(typeof shaped?.code === "string" ? { code: shaped.code } : {}),
    ...(url ? { url } : {}),
  });
  // Inertia rejects its request promise exactly when this event survives;
  // preventing it is what removes the unhandled rejection.
  event.preventDefault();
}

function onHttpException(event: Event): void {
  const response = (
    event as CustomEvent<{ response?: { status?: number; headers?: Record<string, unknown> } }>
  ).detail?.response;

  // A response with the x-inertia header (a validation error above all)
  // continues into the page's onError handling; reporting it here would
  // double-report what the UI already shows.
  const headers = response?.headers;
  if (headers && ("x-inertia" in headers || "X-Inertia" in headers)) {
    return;
  }

  // The event detail carries status, data and headers only — no request
  // URL exists on this event, so the report pins the failure by status.
  report(new Error(`Inertia visit failed with HTTP status ${response?.status ?? "unknown"}`), {
    operation: "httpException",
    ...(response?.status !== undefined ? { status: response.status } : {}),
  });
}

/**
 * Registers the two listeners and is a no-op without a browser (SSR).
 * Returns the teardown, so tests can detach again.
 */
export function initRouterErrorReporting(): () => void {
  if (typeof document === "undefined") {
    return () => {};
  }

  document.addEventListener("inertia:networkError", onNetworkError);
  document.addEventListener("inertia:httpException", onHttpException);

  return () => {
    document.removeEventListener("inertia:networkError", onNetworkError);
    document.removeEventListener("inertia:httpException", onHttpException);
  };
}
